import { useCallback, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { ParkGeo, ParkRevenue } from "../lib/api.ts";
import { BRAND_COLOR, money, num } from "../lib/format.ts";
/* Тип пары переводов приходит под именем `Pair`: имя `L` в этом файле занято
   самим Leaflet. */
import { t, type L as Pair } from "../i18n/lang.ts";
import { useThemeTick } from "./chrome.tsx";

/**
 * Interactive park map.
 *
 * Three things it has to get right that a generic map component would not:
 *
 *  - **Every park must be identifiable without hovering.** Four of six parks
 *    are Leo & Loona, so brand colour alone leaves four identical dots. Each
 *    pin therefore carries a permanent name label; colour still says brand,
 *    size still says revenue.
 *
 *  - **Leo & Loona DFC and Hello Park sit in the SAME mall**, so their
 *    coordinates are identical and one pin hides the other. Pins sharing a
 *    venue are fanned apart by a fixed number of PIXELS, recomputed on every
 *    zoom: at city zoom the offset is metres, at country zoom it would have
 *    been invisible. A hairline runs back to the true point so the map never
 *    silently claims a park stands somewhere it does not.
 *
 *  - **A park with no revenue is not a park we have no access to.** Four of
 *    six return nothing from Syrve because the API user lacks rights, and a
 *    zero on a map reads as "sold nothing". Those pins are drawn hollow and
 *    dashed, and the legend says what that means.
 *
 * Basemap: Esri Dark Gray Canvas, which needs no API key. CARTO's tiles were
 * used until they closed anonymous access and started stamping "API KEY
 * REQUIRED" across every tile. A key would work, but in a browser app it ships
 * to the client anyway, so a keyless provider is one less secret to rotate.
 */

/** Проза этой карты. Один блок в начале файла — см. App.tsx. */
const TX = {
  mapLabel: { ru: "Карта расположения парков", en: "Park locations map" },
  posRevenue: { ru: "Касса", en: "POS revenue" },
  rowTickets: { ru: "· билеты", en: "· tickets" },
  rowFood: { ru: "· еда", en: "· food" },
  rowBanquet: { ru: "· чеки праздников", en: "· party checks" },
  ticketsSold: { ru: "Билетов продано", en: "Tickets sold" },
  onlineTickets: { ru: "Онлайн-билеты", en: "Online tickets" },
  noSyrveAccess: {
    ru: "К этому парку доступа в Syrve пока нет — это не нулевые продажи.",
    en: "No Syrve access for this park yet — this is not zero sales.",
  },
  sharesVenue: {
    ru: "Делит здание с другим парком; пин сдвинут в сторону, чтобы были видны оба.",
    en: "Shares the building with another park; the pin is nudged aside so both stay visible.",
  },
  legendNoPos: { ru: "доступа к кассе пока нет", en: "no POS access yet" },
  legendDotSize: { ru: "размер точки = касса", en: "dot size = POS revenue" },
} satisfies Record<string, Pair>;

type Props = {
  parks: ParkGeo[];
  revenue: ParkRevenue | null;
  onSelect?: (parkKey: string) => void;
};

const TILE_BASE =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const TILE_LABELS =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const TILE_ATTRIB = "Esri, HERE, Garmin, © OpenStreetMap contributors";

/** Разброс пинов одного здания — в ПИКСЕЛЯХ, не в градусах: см. шапку файла. */
const FAN_PX = 30;

/**
 * Короткие подписи. Полное имя не помещается рядом с точкой, а обрезанное
 * читается хуже, чем осмысленное сокращение, которое в парках и так в ходу.
 */
const SHORT_NAME: Record<string, string> = {
  ll_dfc: "L&L DFC",
  ll_dalma: "L&L Dalma",
  ll_yas: "L&L Yas",
  ll_bahrain: "L&L Bahrain",
  hellopark: "Hello Park",
  zamania: "Zamania",
};

type Direction = "right" | "left" | "top" | "bottom";
type Box = { x0: number; y0: number; x1: number; y1: number };

function overlaps(a: Box, b: Box): boolean {
  return a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
}

function inside(b: Box, size: L.Point): boolean {
  return b.x0 >= 2 && b.y0 >= 2 && b.x1 <= size.x - 2 && b.y1 <= size.y - 2;
}

/** Для парка, добавленного после этого кода, подпись выводится из названия. */
function shortName(park: ParkGeo): string {
  return SHORT_NAME[park.park_key] ?? park.name.replace(/\s+Mall$/i, "");
}

export function ParkMap({ parks, revenue, onSelect }: Props): React.ReactElement {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  /**
   * Пины и всплывающие карточки рисует Leaflet, а не React, поэтому смена
   * языка сама их не перерисует: разметка попапа — строка, собранная один раз.
   * `useThemeTick` следит за атрибутом `data-lang` на документе тем же
   * механизмом, которым графики перерисовываются после смены темы, — и добавлен
   * в зависимости `draw` ниже. Без него после переключения языка карточка парка
   * осталась бы на прежнем языке до следующего зума.
   */
  const tick = useThemeTick();

  /**
   * Обработчик выбора живёт в ref, а не в зависимостях эффекта.
   *
   * Родитель передаёт стрелку прямо в JSX, поэтому её тождество меняется на
   * каждом рендере. Пока эффект инициализации зависел от неё, карта
   * уничтожалась и собиралась заново после каждого обновления данных —
   * приближение, которое только что сделал пользователь, откатывалось к
   * общему виду Залива. Карта создаётся один раз.
   */
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  /**
   * Чей попап открыт — переживает перерисовку.
   *
   * Щелчок по пину открывает карточку И ставит фильтр по парку. Фильтр уходит
   * наверх, экран перезапрашивает данные, приходит новый `revenue` — и `draw`
   * пересоздаётся. А `draw` начинается с `clearLayers()`: маркер уничтожается,
   * карточка вместе с ним. Видна она была ровно столько, сколько шёл запрос, —
   * примерно полсекунды.
   *
   * Запретить перерисовку нельзя: числа в карточке обязаны обновиться вместе с
   * данными. Поэтому запоминаем ключ парка и открываем карточку заново на новом
   * маркере — с новыми числами. Тем же заодно чинится потеря карточки при зуме.
   */
  const openKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return;
    const map = L.map(hostRef.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false, // иначе колесо страницы залипает на карте
    });
    /**
     * Стартовый вид задаётся сразу: пины считают свои экранные координаты, а
     * Leaflet до setView не знает ни центра, ни зума и падает с «Set map center
     * and zoom first». Точная рамка приедет из fitBounds, когда придут парки.
     */
    map.setView([25.2, 53.5], 6);
    L.tileLayer(TILE_BASE, { attribution: TILE_ATTRIB, maxZoom: 19 }).addTo(map);
    // Названия городов — отдельным слоем поверх пинов было бы нечитаемо, поэтому
    // подписи карты идут под ними: парк важнее подложки.
    L.tileLayer(TILE_LABELS, { maxZoom: 19, opacity: 0.85 }).addTo(map);
    map.on("click", () => {
      // Щелчок по маркеру до карты не доходит, поэтому это всегда «мимо пина».
      openKeyRef.current = null;
      onSelectRef.current?.("");
    });
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  const draw = useCallback(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer || parks.length === 0) return;
    /* Снимаем ДО очистки слоя: уничтожение маркера закрывает попап и обнуляет
       ссылку, а восстанавливать надо по тому, что было до перерисовки. */
    const reopen = openKeyRef.current;
    layer.clearLayers();

    const byKey = new Map(revenue?.parks.map((p) => [p.park_key, p]) ?? []);
    const coverage = new Map(revenue?.coverage.map((c) => [c.park_key, c]) ?? []);
    const max = Math.max(1, ...(revenue?.parks ?? []).map((p) => Number(p.revenue_aed_minor)));

    const venueCount = new Map<string, number>();
    for (const p of parks) venueCount.set(p.venue_name, (venueCount.get(p.venue_name) ?? 0) + 1);
    const venueSeen = new Map<string, number>();

    const zoom = map.getZoom();

    /** Первый проход: где стоит каждый пин и какого он размера. */
    const pins = parks.map((park) => {
      const total = venueCount.get(park.venue_name) ?? 1;
      const index = venueSeen.get(park.venue_name) ?? 0;
      venueSeen.set(park.venue_name, index + 1);

      const truePoint = L.latLng(Number(park.latitude), Number(park.longitude));
      let point = truePoint;
      if (total > 1) {
        const angle = (2 * Math.PI * index) / total - Math.PI / 2;
        const px = map.project(truePoint, zoom);
        point = map.unproject(
          L.point(px.x + FAN_PX * Math.cos(angle), px.y + FAN_PX * Math.sin(angle)),
          zoom,
        );
      }

      const rev = byKey.get(park.park_key);
      const revMinor = Number(rev?.revenue_aed_minor ?? 0);
      const hasData = (coverage.get(park.park_key)?.last_day ?? null) !== null;
      // Размер точки — доля от максимума, но с полом: парк не должен исчезать.
      const size = hasData ? 16 + Math.round(18 * Math.sqrt(revMinor / max)) : 14;

      return {
        park,
        rev,
        hasData,
        size,
        point,
        truePoint,
        fanned: total > 1,
        color: BRAND_COLOR[park.brand_key] ?? "#00C1DE",
        label: shortName(park),
        screen: map.latLngToContainerPoint(point),
      };
    });

    /**
     * Второй проход: куда положить подписи.
     *
     * Все шесть парков жмутся к двум точкам побережья, и подписи, выставленные
     * в одну сторону, наезжают друг на друга — карта становится нечитаемой ровно
     * там, где смотреть интереснее всего. Поэтому для каждой подписи
     * перебираются четыре стороны и берётся первая свободная: жадная раскладка,
     * зато пересчитывается на каждом зуме и не требует ручной правки, когда
     * появится седьмой парк.
     */
    const canvas = map.getSize();
    const boxes: Box[] = pins.map((p) => ({
      x0: p.screen.x - p.size / 2 - 2,
      y0: p.screen.y - p.size / 2 - 2,
      x1: p.screen.x + p.size / 2 + 2,
      y1: p.screen.y + p.size / 2 + 2,
    }));

    const placements = pins.map((p) => {
      const w = p.label.length * 6.4 + 26;
      const h = 19;
      const gap = p.size / 2 + 6;
      const x = p.screen.x;
      const y = p.screen.y;
      const right = { dir: "right" as Direction, box: { x0: x + gap, y0: y - h / 2, x1: x + gap + w, y1: y + h / 2 } };
      const options: Array<{ dir: Direction; box: Box }> = [
        right,
        { dir: "left",   box: { x0: x - gap - w, y0: y - h / 2,   x1: x - gap,     y1: y + h / 2 } },
        { dir: "top",    box: { x0: x - w / 2,   y0: y - gap - h, x1: x + w / 2,   y1: y - gap } },
        { dir: "bottom", box: { x0: x - w / 2,   y0: y + gap,     x1: x + w / 2,   y1: y + gap + h } },
      ];
      // Если свободного места нет вовсе, подпись всё равно ставится справа:
      // наложение лучше, чем безымянная точка.
      const free =
        options.find((o) => inside(o.box, canvas) && !boxes.some((b) => overlaps(b, o.box))) ?? right;
      boxes.push(free.box);
      return free.dir;
    });

    pins.forEach((p, i) => {
      if (p.fanned) {
        // Волосок к настоящей точке здания — чтобы разнос читался как приём
        // отображения, а не как координаты парка.
        layer.addLayer(
          L.polyline([p.truePoint, p.point], {
            color: p.color,
            weight: 1,
            opacity: 0.45,
            dashArray: "2,3",
            interactive: false,
          }),
        );
        layer.addLayer(
          L.circleMarker(p.truePoint, {
            radius: 2,
            color: p.color,
            weight: 1,
            opacity: 0.6,
            fillOpacity: 0.6,
            interactive: false,
          }),
        );
      }

      const icon = L.divIcon({
        className: "",
        iconSize: [p.size, p.size],
        iconAnchor: [p.size / 2, p.size / 2],
        html: `<div class="park-pin" style="
                 width:${p.size}px;height:${p.size}px;
                 background:${p.hasData ? p.color : `${p.color}26`};
                 border-color:${p.hasData ? "var(--bg)" : p.color};
                 border-style:${p.hasData ? "solid" : "dashed"};
               "></div>`,
      });

      const popup = `
        <div class="park-popup">
          <h4>${escapeHtml(p.park.name)}</h4>
          <div class="meta">${escapeHtml(p.park.brand_name)} · ${escapeHtml(p.park.city)} · ${escapeHtml(p.park.venue_name)}</div>
          ${
            p.hasData
              ? `<div class="row"><span>${t(TX.posRevenue)}</span><b>${money(p.rev?.revenue_aed_minor ?? 0, "AED")}</b></div>
                 <div class="row"><span>${t(TX.rowTickets)}</span><b>${money(p.rev?.tickets_aed_minor ?? 0, "AED")}</b></div>
                 <div class="row"><span>${t(TX.rowFood)}</span><b>${money(p.rev?.food_aed_minor ?? 0, "AED")}</b></div>
                 <div class="row"><span>${t(TX.rowBanquet)}</span><b>${money(p.rev?.banquet_aed_minor ?? 0, "AED")}</b></div>
                 <div class="row"><span>${t(TX.ticketsSold)}</span><b>${num(p.rev?.tickets_sold ?? 0)}</b></div>
                 <div class="row"><span>${t(TX.onlineTickets)}</span><b>${money(p.rev?.online_revenue_aed_minor ?? 0, "AED")}</b></div>`
              : `<div class="row" style="color:var(--warn)">${t(TX.noSyrveAccess)}</div>`
          }
          ${p.park.shares_venue ? `<div class="meta" style="margin-top:8px">${t(TX.sharesVenue)}</div>` : ""}
        </div>`;

      const dir = placements[i];
      const gap = p.size / 2 + 6;
      const offset =
        dir === "right" ? L.point(gap, 0)
        : dir === "left" ? L.point(-gap, 0)
        : dir === "top" ? L.point(0, -gap)
        : L.point(0, gap);

      const marker = L.marker(p.point, { icon, title: p.park.name })
        .bindPopup(popup)
        .bindTooltip(`<i style="background:${p.color}"></i>${escapeHtml(p.label)}`, {
          permanent: true,
          direction: dir,
          offset,
          className: `park-label${p.hasData ? "" : " no-data"}`,
          opacity: 1,
        })
        .on("click", () => {
          openKeyRef.current = p.park.park_key;
          onSelectRef.current?.(p.park.park_key);
        })
        /* Закрытие крестиком или щелчком мимо — штатное поведение Leaflet;
           нам остаётся только не восстанавливать карточку после него. */
        .on("popupclose", () => {
          if (openKeyRef.current === p.park.park_key) openKeyRef.current = null;
        });
      layer.addLayer(marker);

      if (reopen === p.park.park_key) {
        marker.openPopup();
        openKeyRef.current = reopen;
      }
    });
  }, [parks, revenue, tick]);

  /** Разброс задан в пикселях, значит после каждого зума его надо пересчитать. */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    draw();
    map.on("zoomend", draw);
    return () => {
      map.off("zoomend", draw);
    };
  }, [draw]);

  /**
   * Кадрирование — только при смене СОСТАВА парков.
   *
   * Ключом идёт список park_key, а не сам массив: при выборе парка в фильтре
   * приходит новый массив с теми же парками, и зависимость от объекта
   * возвращала карту к обзору Залива каждый раз, когда пользователь кликал по
   * пину, — то есть ровно тогда, когда он только что приблизил нужное место.
   */
  const parksRef = useRef(parks);
  parksRef.current = parks;
  const parksKey = parks.map((p) => p.park_key).join(",");

  useEffect(() => {
    const map = mapRef.current;
    const list = parksRef.current;
    if (!map || list.length === 0) return;
    map.fitBounds(
      L.latLngBounds(list.map((p) => [Number(p.latitude), Number(p.longitude)] as L.LatLngExpression)).pad(0.45),
    );
  }, [parksKey]);

  return (
    <div className="map-block">
      <div className="map-wrap" ref={hostRef} role="application" aria-label={t(TX.mapLabel)} />
      <div className="map-legend">
        {/* Названия брендов не переводятся ни на каком языке — это данные. */}
        <span className="k"><i className="dot" style={{ background: BRAND_COLOR.leoloona }} />Leo &amp; Loona</span>
        <span className="k"><i className="dot" style={{ background: BRAND_COLOR.hellopark }} />Hello Park</span>
        <span className="k"><i className="dot" style={{ background: BRAND_COLOR.zamania }} />Zamania</span>
        <span className="k"><i className="dot hollow" />{t(TX.legendNoPos)}</span>
        <span className="k note">{t(TX.legendDotSize)}</span>
      </div>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );
}
